import { Component, Story, Knobs } from './DocTypes';

const PlaygroundKnobs: Knobs = {
  multiple: {
    default: true,
    editor: 'checkbox',
  },
  allowDragDrop: {
    default: false,
    editor: 'checkbox',
  },
  large: {
    default: false,
    editor: 'checkbox',
  },
  disabled: {
    default: false,
    editor: 'checkbox',
  },
  accept: {
    default: '',
    editor: 'textbox',
  },
  dragDropMessage: {
    default: '',
    editor: 'textbox',
  },
  buttonText: {
    default: '',
    editor: 'textbox',
  },
  clearButtonText: {
    default: 'Clear',
    editor: 'textbox',
  },
  multipleFilesCaption: {
    default: '{{fileCount}} files selected',
    editor: 'textbox',
  },
};

const deviceAndDarkModeKnobsV3: Knobs = {
  colourScheme: {
    editor: 'dropdown',
    editorOptions: ['light', 'dark', 'default', ''],
    default: '',
  },
  device: {
    editor: 'dropdown',
    editorOptions: ['mobile', 'desktop', ''],
    default: '',
  },
};

const PlaygroundStory: Story = {
  title: 'Playground',
  description: 'A tag-file-picker where you can change all the attributes',
  coreComponent: {
    html: `<tag-file-picker{{...knobs}}>
</tag-file-picker>`,
    knobs: PlaygroundKnobs,
  },

  coreComponent_v3: {
    html: `<tag3-file-picker {{...knobs}}>
    </tag3-file-picker>`,
    knobs: {
      ...PlaygroundKnobs,
      ...deviceAndDarkModeKnobsV3,
    },
  },
  reactWrapper: {
    jsx: `<TagFilePicker{{...knobs}} />`,
    knobs: PlaygroundKnobs,
  },
  reactWrapper_v3: {
    jsx: `<Tag3FilePicker{{...knobs}} />`,
    knobs: {
      ...PlaygroundKnobs,
      ...deviceAndDarkModeKnobsV3,
    },
  },
};

const CustomStylesKnobs: Knobs = {
  multiple: {
    default: true,
    editor: 'checkbox',
  },
  allowDragDrop: {
    default: true,
    editor: 'checkbox',
  },
  large: {
    default: true,
    editor: 'checkbox',
  },
  accept: {
    default: '',
    editor: 'textbox',
  },
};

const CustomStylesStory: Story = {
  title: 'Custom Styles',
  description: 'A tag-file-picker with custom styles',
  coreComponent: {
    html: `<tag-file-picker {{...knobs}}'>
</tag-file-picker>`,
    knobs: {
      ...CustomStylesKnobs,
      dragContainerStyle: {
        default: '{"background-color": "transparent", "border":"none"}',
        editor: 'multilinetextbox',
      },
    },
  },
  coreComponent_v3: {
    html: `<tag3-file-picker {{...knobs}}'>
</tag3-file-picker>`,
    knobs: {
      ...CustomStylesKnobs,
      dragContainerStyle: {
        default: '{"background-color": "transparent", "border":"none"}',
        editor: 'multilinetextbox',
      },
      ...deviceAndDarkModeKnobsV3,
    },
  },
  reactWrapper: {
    jsx: `<TagFilePicker{{...knobs}} />`,
    knobs: {
      ...CustomStylesKnobs,
      dragContainerStyle: {
        default: '{backgroundColor: "transparent","border":"none"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
    },
  },
  reactWrapper_v3: {
    jsx: `<Tag3FilePicker{{...knobs}} />`,
    knobs: {
      ...CustomStylesKnobs,
      dragContainerStyle: {
        default: '{backgroundColor: "transparent","border":"none"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
      ...deviceAndDarkModeKnobsV3,
    },
  },
};

export const TagFilePickerDocs: Component = {
  type: 'component',
  name: 'file-picker',
  description: 'Renders a file picker',
  mobileFriendly: false,
  notForMobile: true,
  popular: false,
  preRelease: '',
  props: [
    {
      name: 'id',
      default: 'file',
      description:
        'DEPRECATED in v3.The id of the file input. This is important if there are multiple uploads on the page',
      type: 'string',
    },
    {
      name: 'filePickerId',
      default: 'file',
      description:
        'V3 Only: The id of the file input. This is important if there are multiple uploads on the page',
      type: 'string',
    },
    {
      name: 'multiple',
      description: 'Whether multiple files can be uploaded',
      type: 'boolean',
    },
    {
      name: 'allowDragDrop',
      description: 'Whether a drag drop area is rendered',
      type: 'boolean',
    },
    {
      name: 'large',
      description: 'Whether a large drag drop area is rendered',
      type: 'boolean',
    },
    {
      name: 'dragDropMessage',
      description: 'The message to display in the drop area',
      type: 'string',
      default: 'Drag and drop files here to upload',
    },
    {
      name: 'buttonText',
      description: 'The default text to display on the button',
      type: 'string',
    },
    {
      name: 'clearButtonText',
      description: 'V3 Only: The text to display on the clear button',
      type: 'string',
      default: 'Clear',
    },
    {
      name: 'multipleFilesCaption',
      description:
        'The template text to display when files have been selected. {{fileCount}} is a token that will output the number of files selected',
      type: 'string',
      default: '{{fileCount}} files selected',
    },
    {
      name: 'disabled',
      description: 'Whether the component is disabled',
      type: 'boolean',
      default: 'false',
    },
    {
      name: 'accept',
      description: 'Specifies the types of files that the server accepts',
      type: 'string',
    },
    {
      name: 'dragContainerStyle',
      description: 'JSX style object in JSON to apply to the drag container',
      type: 'string',
    },
    {
      name: 'colour-scheme',
      description:
        'V3 Only: Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'light',
    },
    {
      name: 'device',
      description: 'V3 Only: Set preferred styling theme for device type.',
      type: 'desktop | mobile',
      default: 'desktop',
    },
  ],
  reactPropOverrides: [],
  events: [
    {
      name: 'fileChange',
      description: 'raised when a file is selected',
      parameters: [
        {
          name: 'files',
          description: 'The files that are selected',
          type: 'FileList',
        },
      ],
    },
    {
      name: 'clearClick',
      description: 'Raised when the clear button is clicked',
      parameters: [],
    },
  ],
  methods: [
    {
      name: 'focusButton',
      description: 'Sets the focus to the file picker button',
      parameters: [],
      returnType: 'void',
    },
    {
      name: 'clear',
      description: 'Clears the current files that are selected',
      parameters: [],
      returnType: 'void',
    },
  ],
  stories: [PlaygroundStory, CustomStylesStory],
};
